import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthActions, AuthRedirectService, AuthService, AuthStorageService, AuthToken, RoutingService, StateWithClientAuth, UserIdService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, catchError, map, Observable, of, Subscription, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SsoLogin {
  userId: string | null = null;
  private static readonly PKCE_VERIFIER_STORAGE_KEY = 'code_verifier';
  private static readonly PKCE_VERIFIER_PATTERN = /^[A-Za-z0-9\-._~]{43,128}$/;
  
  private loginErrorMessageSubject = new BehaviorSubject<string | null>(null);
  loginErrorMessage$ = this.loginErrorMessageSubject.asObservable();

  userIdSubscripton = new Subscription();

  constructor(
    private routingService: RoutingService,
    protected userIdService: UserIdService,
    protected authStorageService: AuthStorageService,
    protected authRedirectService: AuthRedirectService,
    private userFacade: UserAccountFacade,
    protected store: Store<StateWithClientAuth>,
    private auth: AuthService,
    @Inject(HttpClient) private http: HttpClient,
  ) {}

  getRedirectUrl(): string {
    return this.routingService.getFullUrl({
      cxRoute: 'ssoLogin',
    });
  }

  storeAuthorizationRequest(verifier: string): void {
    sessionStorage.setItem(SsoLogin.PKCE_VERIFIER_STORAGE_KEY, verifier);
  }

  hasValidAuthorizationCallback(): boolean {
    return this.getPkceVerifier() !== null;
  }

  clearAuthorizationRequest(): void {
    sessionStorage.removeItem(SsoLogin.PKCE_VERIFIER_STORAGE_KEY);
  }

  doSSOLogin(code: string = ''): Observable<boolean> {
    const verifier = this.getPkceVerifier();
    if (!code || !verifier) {
      console.error('SSO Login failed! Missing code or PKCE verifier.');
      this.loginErrorMessageSubject.next('Missing authentication parameters');
      return of(false);
    }

    return this.performUserValidation(code).pipe(
      switchMap((authorizedUserId) => {
        if (!authorizedUserId) {
          return of(false);
        }

        const tokenUrl = environment.oauthTokenUrl;
        const username = environment.oauthClientId;
        const password = environment.oauthClientSecret;

        const headers = new HttpHeaders({
          'Authorization': 'Basic ' + btoa(`${username}:${password}`),
          'Content-Type': 'application/x-www-form-urlencoded'
        });

        
        const body = new URLSearchParams({
          grant_type: 'client_credentials',
          userId: authorizedUserId
        });

        return this.http.post(tokenUrl, body.toString(), { headers }).pipe(
          map((res) => {
            this.authStorageService.setToken(res as AuthToken);
            this.store.dispatch(new AuthActions.Login());
            this.authRedirectService.redirect();
            return true;
          }),
          catchError((error) => {
            console.error('Token API error:', error);
            this.loginErrorMessageSubject.next(error.error?.message || 'An error occurred while fetching token');
            return of(false);
          })
        );
      }),
    );
  }

  /**
   * Validates the user before token is requested
   * Returns authorized userId when validation succeeds, null otherwise
   */
  private performUserValidation(code: string = ''): Observable<string | null> {
    const url = environment.ssoAuthorizeUrl;
    const payload = {
      code: code,
      redirectUri: this.getRedirectUrl(),
      scope: 'openid profile email offline_access',
      codeVerifier: this.getPkceVerifier()
    };

    return this.http.post(url, payload).pipe(
      map((res: any) => {
        if (res?.authenticated === true) {
          this.clearAuthorizationRequest();
          this.userIdService.setUserId(res?.userId || 'anonymous');
          this.userId = res?.userId || null;
          this.loginErrorMessageSubject.next(null);
          return this.userId;
        } else {
          // Clear token immediately on validation failure
          this.clearAuthorizationRequest();
          this.authStorageService.removeItem('access_token');
          this.loginErrorMessageSubject.next(res?.message || 'User validation failed');
          return null;
        }
      }),
      catchError((error) => {
        console.error('User validation error:', error);
        this.clearAuthorizationRequest();
        this.authStorageService.removeItem('access_token');
        this.loginErrorMessageSubject.next(error.error?.errors?.[0]?.message || 'Validation error occurred');
        return of(null); // Return null instead of throwing to allow guard to handle it
      })
    );
  }
  
  private getPkceVerifier(): string | null {
    const verifier = sessionStorage.getItem(SsoLogin.PKCE_VERIFIER_STORAGE_KEY);

    if (!verifier || !SsoLogin.PKCE_VERIFIER_PATTERN.test(verifier)) {
      return null;
    }

    return verifier;
  }

}